import React, { useState, useEffect, useMemo } from 'react';
import { Globe, MapPin, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const MASTER_REGIONS = [
  { id: '1', numericId: 1, name: 'India (IN)', code: 'IN', flag: '🇮🇳' },
  { id: '2', numericId: 2, name: 'United Kingdom (UK)', code: 'UK', flag: '🇬🇧' },
  { id: '3', numericId: 3, name: 'Netherlands (NL)', code: 'NL', flag: '🇳🇱' }
];

export const ScopeSwitcher = () => {
  const { user, isSuperAdmin } = useAuth();

  // Determine if logged in user has explicit Global Access
  const hasGlobalAccess = useMemo(() => {
    if (!user) return true; // Default fallback before auth ready
    if (isSuperAdmin || user.type === 'SuperAdmin') return true;
    if (user.userScope?.isGlobal || user.userScope?.hasGlobalAccess) return true;
    if (Array.isArray(user.regionAssignments) && user.regionAssignments.some(a => a.scope_type === 'GLOBAL')) return true;
    // Unconstrained tenant users default to Global region scope across their tenant
    if (!Array.isArray(user.regionAssignments) || user.regionAssignments.length === 0) return true;
    const rolesList = Array.isArray(user.role) ? user.role : (Array.isArray(user.roles) ? user.roles.map(r => r?.name || r) : [user.role].filter(Boolean));
    if (rolesList.some(r => ['Admin', 'SuperAdmin', 'Client Tenant Admin', 'Tenant Admin', 'CEO', 'Manager'].includes(r))) {
      return true;
    }
    return false;
  }, [user, isSuperAdmin]);

  // Determine explicit allowed region IDs for user
  const allowedRegionIds = useMemo(() => {
    if (hasGlobalAccess) return [1, 2, 3];
    if (user?.userScope?.allowedRegionIds && Array.isArray(user.userScope.allowedRegionIds)) {
      return user.userScope.allowedRegionIds.map(Number);
    }
    if (Array.isArray(user?.regionAssignments) && user.regionAssignments.length > 0) {
      return user.regionAssignments.map(a => Number(a.region_id)).filter(id => Boolean(id) && !isNaN(id));
    }
    return [1]; // Baseline fallback to India
  }, [user, hasGlobalAccess]);

  // Available options for current user
  const availableOptions = useMemo(() => {
    const opts = [];
    if (hasGlobalAccess) {
      opts.push({ id: 'GLOBAL', name: 'Global (All Regions)', code: 'GLOBAL', flag: '🌐' });
    }
    MASTER_REGIONS.forEach(reg => {
      if (hasGlobalAccess || allowedRegionIds.includes(reg.numericId)) {
        opts.push(reg);
      }
    });
    return opts;
  }, [hasGlobalAccess, allowedRegionIds]);

  const [selectedRegionId, setSelectedRegionId] = useState(() => {
    const stored = localStorage.getItem('selectedRegionId') || 'GLOBAL';
    return stored;
  });

  // Enforce RBAC boundary check on selected region
  useEffect(() => {
    if (user) {
      if (!hasGlobalAccess) {
        const primaryAllowedId = String(allowedRegionIds[0] || 1);
        const isCurrentSelectionValid = selectedRegionId !== 'GLOBAL' && allowedRegionIds.includes(Number(selectedRegionId));

        if (!isCurrentSelectionValid) {
          setSelectedRegionId(primaryAllowedId);
          localStorage.setItem('selectedRegionId', primaryAllowedId);
          window.dispatchEvent(new Event('region-scope-changed'));
        }
      }
    }
  }, [user, hasGlobalAccess, allowedRegionIds, selectedRegionId]);

  const handleRegionChange = (e) => {
    const val = e.target.value;
    setSelectedRegionId(val);
    if (val === 'GLOBAL') {
      localStorage.removeItem('selectedRegionId');
    } else {
      localStorage.setItem('selectedRegionId', val);
    }
    // Dispatch custom event for dashboard & API refetching
    window.dispatchEvent(new Event('region-scope-changed'));
  };

  // If user only has access to 1 region and no Global access, render single region badge
  if (!hasGlobalAccess && availableOptions.length === 1) {
    const single = availableOptions[0];
    return (
      <div className="flex items-center space-x-2 bg-slate-800/80 border border-slate-700 text-slate-200 px-3 py-1.5 rounded-lg text-xs font-semibold shadow-2xs">
        <span>{single.flag}</span>
        <span className="text-slate-100 font-bold">{single.name}</span>
        <Lock className="w-3 h-3 text-slate-400 ml-1" title="Region Scope locked by RBAC policy" />
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2 bg-slate-800/80 border border-slate-700 text-slate-200 px-3 py-1.5 rounded-lg text-sm font-medium">
      <Globe className="w-4 h-4 text-blue-400 shrink-0" />
      <span className="text-slate-400 hidden md:inline text-xs font-semibold uppercase tracking-wider">Scope:</span>
      <select
        value={selectedRegionId}
        onChange={handleRegionChange}
        className="bg-transparent text-slate-100 text-xs font-semibold border-none focus:ring-0 focus:outline-none cursor-pointer"
      >
        {availableOptions.map(opt => (
          <option key={opt.id} value={opt.id} className="bg-slate-900 text-slate-100">
            {opt.flag} {opt.name}
          </option>
        ))}
      </select>
    </div>
  );
};

export default ScopeSwitcher;
